/**
 * ISO string <-> Date helpers for form date fields (gpm-webapp-v2's
 * `library/lang/date` pattern, minus the luxon dependency — reipm keeps its
 * dependency tree lean and native `Date` covers these two conversions).
 *
 * Form fields store ISO strings (what the SDK schemas expect); the gpm-ui
 * `DatePicker` speaks native `Date` — `FormDatePicker` converts at the edge.
 */

/** Parse an ISO string into a local Date (for date pickers). Date-only strings parse as LOCAL midnight, not UTC. */
export function isoToDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/**
 * ISO wire string → the `YYYY-MM-DD` shape date-input form fields store
 * (`FormDatePicker` storage; also the UTC calendar-day comparison key the API
 * uses, e.g. `resolveActiveFromWithinContract`). Empty/nullish → `''`.
 */
export function isoToDateInput(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

/** Format a Date as an ISO string for form field storage: full ISO with time, or local `YYYY-MM-DD` without. */
export function dateToIso(d: Date, withTime = false): string {
  if (Number.isNaN(d.getTime())) return '';
  if (withTime) return d.toISOString();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ─── Display formatting (UG-4513, ports reipm-legacy's luxon helpers) ────────
//
// Legacy shape: `MM/dd/yyyy hh:mm a` (e.g. `07/29/2026 02:05 PM`), rendered in
// EST for grid cells with UTC + local variants in the tooltip (`DateTimeCell`).
// Implemented on Intl.DateTimeFormat — no luxon.

export const EST_TIMEZONE = 'America/New_York';

/** Intl formatters are expensive to construct; grids format hundreds of cells. */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (timeZone: string | undefined): Intl.DateTimeFormat => {
  const cacheKey = timeZone ?? 'local';
  let formatter = formatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    formatterCache.set(cacheKey, formatter);
  }
  return formatter;
};

/**
 * Coerce API values to a Date. Zone-less ISO datetime strings are treated as
 * UTC (legacy parity: luxon parsed with `{ zone: 'utc' }`), while `new Date`
 * would read them as local time.
 */
const toDate = (date: Date | string): Date => {
  if (date instanceof Date) return date;
  const zoneless = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(date.trim());
  return new Date(zoneless ? `${date.trim()}Z` : date);
};

const formatInZone = (date: Date | string, timeZone?: string): string => {
  const parsed = toDate(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return getFormatter(timeZone).format(parsed).replace(', ', ' ');
};

/** `07/29/2026 02:05 PM` in EST — the reipm grid-cell standard. */
export const formatEstDateTime = (date: Date | string): string => formatInZone(date, EST_TIMEZONE);

const zoneNameFormatterCache = new Map<string, Intl.DateTimeFormat>();

/** Short name of `timeZone` (the viewer's when omitted) at that instant, e.g. `EDT` or `GMT+4`; '' if unavailable. */
const zoneName = (date: Date, timeZone?: string): string => {
  const cacheKey = timeZone ?? 'local';
  let formatter = zoneNameFormatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' });
    zoneNameFormatterCache.set(cacheKey, formatter);
  }
  return formatter.formatToParts(date).find((part) => part.type === 'timeZoneName')?.value ?? '';
};

/**
 * `10/23/2026 03:45 PM EDT`: {@link formatEstDateTime} + the short America/New_York zone name for
 * that instant (`EDT` in summer, `EST` in winter); '' when invalid. UG-5350 — for times a user acts
 * on (job rejection, technician edit deadline), where an unlabelled EST time reads as local.
 */
export const formatEstDateTimeWithZone = (date: Date | string): string => {
  const parsed = toDate(date);
  const formatted = formatInZone(parsed, EST_TIMEZONE);
  if (!formatted) return '';
  const zone = zoneName(parsed, EST_TIMEZONE);
  return zone ? `${formatted} ${zone}` : formatted;
};

const estDateOnlyFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: EST_TIMEZONE,
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
});

const estTimeOnlyFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: EST_TIMEZONE,
  hour: 'numeric',
  minute: '2-digit',
});

/** `07/29/2026` in EST — the date half of `formatEstDateTime`, for surfaces that split the two (AWA authorization card). */
export const formatEstDate = (date: Date | string): string => {
  const parsed = toDate(date);
  return Number.isNaN(parsed.getTime()) ? '' : estDateOnlyFormatter.format(parsed);
};

/** `2:05 PM` in EST — the time half of `formatEstDateTime` (AWA signature captions). */
export const formatEstTime = (date: Date | string): string => {
  const parsed = toDate(date);
  return Number.isNaN(parsed.getTime()) ? '' : estTimeOnlyFormatter.format(parsed);
};

/**
 * Legacy `dateUSFormat`: `MM/DD/YYYY` from the ISO date part — UTC calendar
 * day, parity with the monolith's `date.toISOString().split('T')[0]`.
 * Empty/invalid dates render as an empty string (legacy passed only valid
 * `Date`s; wire values here are ISO strings that may be null-ish).
 */
export const formatUsDate = (date: Date | string | null | undefined, delimiter = '/'): string => {
  if (!date) return '';
  const parsed = toDate(date);
  if (Number.isNaN(parsed.getTime())) return '';
  const [year, month, day] = parsed.toISOString().split('T')[0].split('-');
  return [month, day, year].join(delimiter);
};

const mediumDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const mediumDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/** `Aug 7, 2026` in the viewer's timezone — recap/summary lines. */
export const formatMediumDate = (date: Date | string): string => {
  const parsed = toDate(date);
  return Number.isNaN(parsed.getTime()) ? '' : mediumDateFormatter.format(parsed);
};

const utcMediumDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/**
 * `Aug 7, 2026` from the UTC calendar day (UG-4621) — for date-ONLY wire
 * values stored as UTC midnight (contract start/end, spec activeFrom), where
 * {@link formatMediumDate}'s local rendering would shift US viewers back a
 * day. The medium-date sibling of {@link formatUsDate}.
 */
export const formatUtcMediumDate = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const parsed = toDate(date);
  return Number.isNaN(parsed.getTime()) ? '' : utcMediumDateFormatter.format(parsed);
};

const utcMonthDayFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
});

/** `Aug 7` from the UTC calendar day — the year-less half of {@link formatUtcMediumDate} (contract term ranges). */
export const formatUtcMonthDay = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const parsed = toDate(date);
  return Number.isNaN(parsed.getTime()) ? '' : utcMonthDayFormatter.format(parsed);
};

/** `Aug 7, 2026, 2:05 PM` in the viewer's timezone — recap/summary lines. */
export const formatMediumDateTime = (date: Date | string): string => {
  const parsed = toDate(date);
  return Number.isNaN(parsed.getTime()) ? '' : mediumDateTimeFormatter.format(parsed);
};

const timeOnlyFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});

/** `2:38 PM` in the viewer's timezone — e.g. the photo viewer's Captured column. */
export const formatTimeOnly = (date: Date | string): string => {
  const parsed = toDate(date);
  return Number.isNaN(parsed.getTime()) ? '' : timeOnlyFormatter.format(parsed);
};

/** `Today, 2:41 PM` when the instant falls on the viewer's current calendar day, else `formatMediumDateTime` (UG-4584 M5). */
export const formatTodayAwareDateTime = (date: Date | string): string => {
  const parsed = toDate(date);
  if (Number.isNaN(parsed.getTime())) return '';
  const now = new Date();
  const isToday =
    parsed.getFullYear() === now.getFullYear() &&
    parsed.getMonth() === now.getMonth() &&
    parsed.getDate() === now.getDate();
  return isToday ? `Today, ${timeOnlyFormatter.format(parsed)}` : mediumDateTimeFormatter.format(parsed);
};

/** Same instant in UTC, suffixed ` UTC`. */
export const formatUtcDateTime = (date: Date | string): string => {
  const formatted = formatInZone(date, 'UTC');
  return formatted ? `${formatted} UTC` : '';
};

/** Same instant in the viewer's timezone, suffixed with its short zone name, e.g. `(GMT+4)`. */
export const formatLocalDateTime = (date: Date | string): string => {
  const parsed = toDate(date);
  const formatted = formatInZone(parsed);
  if (!formatted) return '';
  const zone = zoneName(parsed);
  return zone ? `${formatted} (${zone})` : formatted;
};
